import { useState } from 'react'
import { Eye } from 'lucide-react'
import { cn } from '@shared/lib/utils'

interface WizardPreviewPanelProps {
  /** Render prop — receives isMobileView boolean, returns preview content */
  preview: (isMobileView: boolean) => React.ReactNode
  /** Optional text shown below the browser chrome frame */
  infoText?: React.ReactNode
  /** Optional extra content below the info text */
  footer?: React.ReactNode
  className?: string
}

export function WizardPreviewPanel({
  preview,
  infoText,
  footer,
  className,
}: WizardPreviewPanelProps) {
  const [isMobileView, setIsMobileView] = useState(false)

  return (
    <div className={cn('flex-1 h-full flex flex-col pt-6 pb-6 pl-3 pr-6', className)}>
      {/* Inner bordered card — fills remaining height */}
      <div className="flex-1 border border-[#e7ebec] rounded-[24px] p-8 flex flex-col overflow-hidden">

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto flex flex-col items-center scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">

          {/* Preview frame */}
          {isMobileView ? (
            <div className="py-6 flex items-center justify-center w-full">
              {preview(true)}
            </div>
          ) : (
            <div className="w-full max-w-[620px] bg-white border border-slate-100 rounded-[20px] shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_10px_10px_-5px_rgba(0,0,0,0.04)] overflow-hidden">
              {/* Browser chrome */}
              <div className="border-b border-slate-100 px-5 py-4 flex items-center gap-[4.8px]">
                <div className="w-2 h-2 rounded-full bg-slate-300" />
                <div className="w-2 h-2 rounded-full bg-slate-300" />
                <div className="w-2 h-2 rounded-full bg-slate-300" />
              </div>
              {/* Preview content */}
              <div className="min-h-[400px] max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">
                {preview(false)}
              </div>
            </div>
          )}

          {/* Info text */}
          {infoText && (
            <p className="text-center text-sm text-[#5c7c82] mt-6 leading-relaxed max-w-[480px]">
              {infoText}
            </p>
          )}

          {footer}
        </div>

        {/* Mobile view toggle — pinned at bottom of card */}
        <div className="shrink-0 flex justify-center pt-6">
          <button
            type="button"
            onClick={() => setIsMobileView(v => !v)}
            className={cn(
              'flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm transition-all',
              isMobileView
                ? 'bg-[#0b3b45] text-white border-[#0b3b45]'
                : 'bg-white text-[#0b3b45] border-[#e7ebec] hover:bg-[#f4f6f6]'
            )}
          >
            <Eye className="h-4 w-4" />
            Mobile view
          </button>
        </div>

      </div>
    </div>
  )
}
